// src/pages/NotificationSetting.jsx
import React, { Fragment, useState, useEffect, useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Leftnav from "../components/Leftnav";
import Appfooter from "../components/Appfooter";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import { useAPI } from "../context/ApiContext";
import { SnackbarContext } from "../context/SnackbarContext";

const useStyles = makeStyles(() => ({
  root: { marginTop: 30, minWidth: "100%" },
}));

const NotificationSetting = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { newSnack } = useContext(SnackbarContext);

  const {
    isAuthenticated,
    me,            // { userId, ... } が想定
    profile,       // { userId, ... } フォールバック用
    myUserId,      // 環境により存在する場合あり
    getUserNotificationSettings,
    updateMyNotificationSettings,
    _request,      // 念のためのフォールバック
  } = useAPI();

  const currentUserId = useMemo(
    () => me?.userId || myUserId || profile?.userId || null,
    [me?.userId, myUserId, profile?.userId]
  );

  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // 設定の取得
  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!currentUserId) { setLoading(false); return; }
      setLoading(true);
      try {
        const data =
          typeof getUserNotificationSettings === "function"
            ? await getUserNotificationSettings(currentUserId)
            : await _request(`/users/${encodeURIComponent(currentUserId)}/notification-settings/`, { auth: false });
        if (mounted) setSettings(data || {});
      } catch (e) {
        newSnack("error", t("NotificationSetting104") || "読み込みに失敗しました");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    run();
    return () => { mounted = false; };
  }, [currentUserId, getUserNotificationSettings, _request, newSnack, t]);

  const handleToggle = async (key) => {
    if (!settings || saving) return;
    const nextValue = !settings[key];
    setSettings(prev => ({ ...prev, [key]: nextValue }));
    setSaving(true);
    try {
      const updated =
        typeof updateMyNotificationSettings === "function"
          ? await updateMyNotificationSettings({ [key]: nextValue }) // ApiContext 側で body に詰めて PATCH 送信
          : await _request("/users/me/notification-settings/", { method: "PATCH", body: { [key]: nextValue } });
      setSettings(prev => ({ ...prev, ...(updated || {}) }));
    } catch (e) {
      // 失敗したらロールバック
      setSettings(prev => ({ ...prev, [key]: !nextValue }));
      newSnack("error", t("NotificationSetting103") || "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const Row = ({ iconClass, label, on, onClick, disabled }) => (
    <li className="list-inline-item d-block border-bottom me-0">
      <div className="pt-2 pb-2 d-flex align-items-center">
        <i className={`btn-round-md bg-primary-gradiant text-white ${iconClass} font-md me-3`} />
        <h4 className="fw-600 font-xsss mb-0 mt-0">{label}</h4>
        <i
          className={`font-xs text-grey-600 ms-auto mt-3 ${disabled ? "opacity-50" : "pointer"} ${on ? "feather-bell" : "feather-bell-off"}`}
          onClick={!disabled ? onClick : undefined}
          title={on ? (t("NotificationSetting101") || "オフにする") : (t("NotificationSetting102") || "オンにする")}
          aria-disabled={disabled}
        />
      </div>
    </li>
  );

  return (
    <Fragment>
      <Header />
      <Leftnav />
      <div className="main-content bg-lightblue theme-dark-bg right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left">
            <div className="middle-wrap">
              <div className="card w-100 border-0 bg-white shadow-xs p-0 mb-4">
                <div className="card-body p-4 w-100 bg-current border-0 d-flex rounded-3">
                  <Link to="/settings" className="d-inline-block mt-2">
                    <i className="ti-arrow-left font-sm text-white"></i>
                  </Link>
                  <h4 className="font-xs text-white fw-600 ms-4 mb-0 mt-2">
                    {t("NotificationSetting1") || "通知設定"}
                  </h4>
                  {(saving || loading) && (
                    <span className="ms-auto font-xssss text-white-50">{saving ? "Saving…" : "Loading…"}</span>
                  )}
                </div>

                <div className="card-body p-lg-5 p-4 w-100 border-0">
                  <div className="nav-caption fw-600 font-xsss text-grey-500 mb-2">
                    アプリ内通知設定
                  </div>

                  {loading || !settings ? (
                    <div className="font-xssss text-grey-500">読み込み中…</div>
                  ) : (
                    <ul className="list-inline mb-4">
                      <Row
                        iconClass="feather-message-square"
                        label={t("NotificationSetting4") || "コメントされたとき"}
                        on={!!settings.commented_on}
                        onClick={() => handleToggle("commented_on")}
                        disabled={!isAuthenticated}
                      />
                      <Row
                        iconClass="feather-repeat"
                        label={t("NotificationSetting5") || "リポストされたとき"}
                        on={!!settings.reposted_on}
                        onClick={() => handleToggle("reposted_on")}
                        disabled={!isAuthenticated}
                      />
                      <Row
                        iconClass="feather-thumbs-up"
                        label={t("NotificationSetting3") || "Nice されたとき"}
                        on={!!settings.niced_on}
                        onClick={() => handleToggle("niced_on")}
                        disabled={!isAuthenticated}
                      />
                      <Row
                        iconClass="feather-user"
                        label={t("NotificationSetting13") || "フォローされたとき"}
                        on={!!settings.followed_on}
                        onClick={() => handleToggle("followed_on")}
                        disabled={!isAuthenticated}
                      />
                      <Row
                        iconClass="feather-message-circle"
                        label={t("NotificationSetting105") || "自分がコメントした投稿に新しいコメントが付いたとき"}
                        on={!!settings.commented_thread_on}
                        onClick={() => handleToggle("commented_thread_on")}
                        disabled={!isAuthenticated}
                      />
                    </ul>
                  )}

                  {!isAuthenticated && (
                    <div className="alert alert-info font-xssss mt-2">
                      {t("Header14") || "ログインが必要です"}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>  
      <Appfooter />
    </Fragment>
  );
};

export default NotificationSetting;
